if (!customElements.get('wine-cask-article')) {
  customElements.define(
    'wine-cask-article',
    class WineCaskArticle extends HTMLElement {
      connectedCallback() {
        this.progress = this.querySelector('[data-wca-progress]');
        this.navLinks = Array.from(this.querySelectorAll('[data-wca-nav]'));
        this.sections = this.navLinks
          .map((link) => {
            const id = link.getAttribute('href');
            return id ? this.querySelector(id) : null;
          })
          .filter(Boolean);

        this.onScroll = this.onScroll.bind(this);
        this.onTabClick = this.onTabClick.bind(this);
        this.onTabKey = this.onTabKey.bind(this);
        this.onFilterClick = this.onFilterClick.bind(this);
        this.syncStickyTop = this.syncStickyTop.bind(this);

        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();

        this.querySelectorAll('[data-wca-tab]').forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onTabKey);
        });

        this.querySelectorAll('[data-wca-filter]').forEach((button) => {
          button.addEventListener('click', this.onFilterClick);
        });

        this.loadBottlesIfNeeded();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }

      onScroll() {
        this.syncStickyTop();

        const start = this.offsetTop;
        const height = this.offsetHeight - window.innerHeight;
        const value = height > 0 ? Math.min(1, Math.max(0, (window.scrollY - start) / height)) : 0;
        if (this.progress) this.progress.style.width = `${value * 100}%`;

        let current = this.sections[0];
        this.sections.forEach((section) => {
          const top = section.getBoundingClientRect().top;
          if (top < window.innerHeight * 0.38) current = section;
        });

        this.navLinks.forEach((link) => {
          const active = current && link.getAttribute('href') === `#${current.id}`;
          link.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      syncStickyTop() {
        const header = document.querySelector('#shopify-section-header .header') || document.querySelector('store-header');
        if (!header) return;
        const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        this.style.setProperty('--wca-header-end', `${bottom}px`);
      }

      onTabClick(event) {
        this.activateTab(event.currentTarget);
      }

      onTabKey(event) {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const tabs = Array.from(this.querySelectorAll('[data-wca-tab]'));
        const index = tabs.indexOf(event.currentTarget);
        if (index < 0) return;
        event.preventDefault();
        const next =
          event.key === 'ArrowRight'
            ? tabs[(index + 1) % tabs.length]
            : tabs[(index - 1 + tabs.length) % tabs.length];
        next.focus();
        this.activateTab(next);
      }

      activateTab(tab) {
        const group = tab.closest('[data-wca-flavour]');
        if (!group) return;
        const id = tab.getAttribute('aria-controls');
        group.querySelectorAll('[data-wca-tab]').forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
          item.tabIndex = selected ? 0 : -1;
        });
        group.querySelectorAll('[data-wca-panel]').forEach((panel) => {
          panel.hidden = panel.id !== id;
        });
      }

      onFilterClick(event) {
        const button = event.currentTarget;
        const key = button.getAttribute('data-wca-filter');
        this.querySelectorAll('[data-wca-filter]').forEach((item) => {
          item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
        });
        this.querySelectorAll('[data-wca-product]').forEach((item) => {
          item.hidden = key !== 'all' && item.getAttribute('data-wca-cask') !== key;
        });
      }

      caskKey(title) {
        const value = (title || '').toLowerCase();
        if (value.includes('sauternes')) return 'sauternes';
        if (value.includes('lagrein')) return 'lagrein';
        if (value.includes('ibisco')) return 'ibisco';
        if (value.includes('distillery edition')) return 'distillery';
        if (value.includes('cask strength')) return 'cask-strength';
        return 'other';
      }

      isTargetBottle(product) {
        const title = (product.title || '').toLowerCase();
        const sku = `${product.sku || ''} ${product.barcode || ''}`.toUpperCase();
        if (['645264', '645272', '645273', '645275', '645274'].some((code) => sku.includes(code))) {
          return true;
        }
        if (!title.includes('edradour')) return false;
        return (
          title.includes('lagrein') ||
          title.includes('ibisco') ||
          title.includes('sauternes') ||
          title.includes('distillery edition') ||
          (title.includes('cask strength') && title.includes('batch'))
        );
      }

      productImage(product) {
        const image = product.featured_image || product.image;
        if (!image) return '';
        if (typeof image === 'string') return image;
        return image.url || image.src || '';
      }

      formatMoney(price, format) {
        if (price == null || price === '') return '';
        const text = String(price);
        if (text.includes('£') || text.includes('$')) return text;
        const cents = Number(text);
        if (Number.isNaN(cents)) return text;
        const amount = (cents / 100).toFixed(2);
        const pattern = format || '£{{amount}}';
        return pattern.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', String(Math.round(cents / 100)));
      }

      async loadBottlesIfNeeded() {
        const root = this.querySelector('[data-wca-bottles]');
        const configEl = this.querySelector('[data-wca-search-config]');
        if (!root || !configEl) return;
        if (root.querySelector('[data-wca-product]')) return;

        let config;
        try {
          config = JSON.parse(configEl.textContent);
        } catch (error) {
          return;
        }

        const queries = config.queries || [];
        const found = new Map();
        const rootUrl = window.Shopify?.routes?.root || '/';

        await Promise.all(
          queries.map(async (query) => {
            try {
              const url = `${rootUrl}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`;
              const response = await fetch(url);
              if (!response.ok) return;
              const data = await response.json();
              const products = data?.resources?.results?.products || [];
              products.forEach((product) => {
                if (!this.isTargetBottle(product)) return;
                const key = product.handle || product.url || product.title;
                if (!found.has(key)) found.set(key, product);
              });
            } catch (error) {
              // Search is a fallback for bottles that are not in the collection yet.
            }
          })
        );

        if (!found.size) return;

        const order = ['cask-strength', 'lagrein', 'ibisco', 'distillery', 'sauternes', 'other'];
        const items = Array.from(found.values()).sort((a, b) => {
          return order.indexOf(this.caskKey(a.title)) - order.indexOf(this.caskKey(b.title));
        });

        const list = root.querySelector('[data-wca-search-grid]');
        if (!list) return;

        items.forEach((product) => {
          const image = this.productImage(product);
          const href = product.url || `${rootUrl}products/${product.handle}`;
          const li = document.createElement('li');
          li.setAttribute('data-wca-product', '');
          li.setAttribute('data-wca-cask', this.caskKey(product.title));

          const link = document.createElement('a');
          link.className = 'wca-tile';
          link.href = href;

          const media = document.createElement('div');
          media.className = 'wca-tile__media';
          if (image) {
            const img = document.createElement('img');
            img.src = image;
            img.alt = product.title || '';
            media.appendChild(img);
          }

          const body = document.createElement('div');
          body.className = 'wca-tile__body';
          const heading = document.createElement('h3');
          heading.className = 'wca-tile__title';
          heading.textContent = product.title || '';
          const price = document.createElement('p');
          price.className = 'wca-tile__price';
          price.textContent = this.formatMoney(product.price, config.moneyFormat);
          body.append(heading, price);

          link.append(media, body);
          li.appendChild(link);
          list.appendChild(li);
        });

        root.hidden = false;
        const empty = this.querySelector('[data-wca-empty]');
        if (empty) empty.hidden = true;
      }
    }
  );
}
